/**
 * Cart permalinks on the storefront → Peak buy links (D260, `docs/STOREFRONT.md` §2 "Cart
 * permalinks", `docs/BUY-LINKS.md` §6).
 *
 * A same-origin anchor to `/cart/{variantId}:{qty},…` (Shopify's cart permalink, "Create cart
 * permalinks", https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks , read
 * 2026-10-17; an optional locale prefix like `/fr/cart/…`) builds its **own** cart from the URL
 * and opens Shopify's checkout with it. D204 left those links alone because handing off the
 * shopper's `cart.js` would sell the wrong items. A Peak buy link builds exactly the permalink's
 * cart server-side, so the script now sends such a click to
 * `{serviceUrl}/b/{publicKey}/{items}{search}` instead — the same items, the same query.
 *
 * Pure: no DOM, no `window`, so the rules are unit-testable in Node. Imports nothing from `@/`.
 */

/** `/{locale}/cart/{items}` — items digits, `:` and `,` only; the locale a subfolder prefix. */
const PERMALINK_PATH =
  /^(?:\/([A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})?))?\/cart\/(\d{1,20}:\d{1,6}(?:,\d{1,20}:\d{1,6})*)\/?$/u;

/** A permalink, as far as the buy link needs it. */
export type PeakPermalink = {
  /** The items segment, exactly as the link carried it (`123:1,456:2`). */
  items: string;
  /** The query, with its `?`, or `''`. */
  search: string;
  /** The locale prefix (`fr`, `en-gb`), lower case, or `null`. */
  locale: string | null;
};

/**
 * The permalink an anchor's `href` points at, or `null` when it is not one: another origin
 * (a `*.myshopify.com` link seen from the custom domain is left alone, like every cross-origin
 * link), another path, or a malformed items segment. `/cart`, `/cart/add`, `/cart/c/{token}` and
 * the rest never match — they have no `{digits}:{digits}` segment.
 */
export function permalinkOf(href: string | null, pageOrigin: string): PeakPermalink | null {
  if (href === null || href.trim() === '') return null;
  let url: URL;
  try {
    url = new URL(href, pageOrigin);
  } catch {
    return null;
  }
  if (url.origin !== pageOrigin) return null;
  let path: string;
  try {
    path = decodeURIComponent(url.pathname);
  } catch {
    return null;
  }
  const match = PERMALINK_PATH.exec(path);
  if (match === null) return null;
  return {
    items: match[2] as string,
    search: url.search,
    locale: match[1] === undefined ? null : match[1].toLowerCase(),
  };
}

/**
 * The buy link for a permalink: `{serviceUrl}/b/{publicKey}/{items}{search}`. A locale prefix
 * travels as `locale=` (unless the query already names one), which the buy link reads as the
 * checkout's language hint and puts back as the prefix on its own fallback.
 */
export function buyLinkHref(
  serviceUrl: string,
  publicKey: string,
  permalink: PeakPermalink,
): string {
  let search = permalink.search;
  if (permalink.locale !== null) {
    const params = new URLSearchParams(search);
    if (!params.has('locale')) {
      params.set('locale', permalink.locale);
      search = `?${params.toString()}`;
    }
  }
  return `${serviceUrl.replace(/\/+$/u, '')}/b/${encodeURIComponent(publicKey)}/${permalink.items}${search}`;
}
